// ProjectDetailedViewContainer.js (hooks version)
import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Axios from 'axios';
import { API_URL } from '../../utils/config/config';

import ProjectDetailView from '../../components/post-security/project_view/project_detail_view/project_detail_view';

import { Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';

const Root = styled('div')(() => ({}));

function ProjectDetailedViewContainer(props) {
  const {
    auth,
    loading,
    detail_view_project_info,
    toggleDetailView,
    pmResourceNames,
    profile_data,
    multiply_by_sfdc_probability,
    // refresh hooks used after save (provided by parent views)
    updateDetailView,         // PM View only
    fetchPMViewData,          // PM View only
    fetchData,                // Project View only
  } = props;

  // --- local UI state (same semantics as class version) ---
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info',
  });

  const [projectEdits, setProjectEdits] = useState({});
  const [assignmentEdits, setAssignmentEdits] = useState([]);

  const showSnack = useCallback((message, type = 'info') => {
    setSnackbar({ open: true, message, type });
  }, []);

  const closeSnack = useCallback(() => {
    setSnackbar(s => ({ ...s, open: false }));
  }, []);

  // Robustly derive PA id for a PM leader name
  const findPAIdForLeader = useCallback(
    (leaderName) => {
      if (!leaderName || !Array.isArray(pmResourceNames)) return null;
      const m =
        pmResourceNames.find(
          p => (p.name || p.full_name) === leaderName
        ) || null;
      // support both shapes: { pa_id } or { pa_id: number-like string }
      const id = m?.pa_id;
      const n = Number(id);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    [pmResourceNames]
  );

  // Small, stable date -> 'YYYY-MM-DD' helper
  const toYMD = useCallback((d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    // Use UTC to avoid TZ shifting; backend expects a date (no time)
    return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()))
      .toISOString()
      .slice(0, 10);
  }, []);

  const tokenHeaders = useCallback(
    async () => {
      const token = await auth.getAccessTokenSilently();
      return {
        withCredentials: true,
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
    },
    [auth]
  );

  // --- lift state up (keeps existing child API contract) ---
  const updateProjectEdits = useCallback((vals) => setProjectEdits(vals || {}), []);
  const updateAssignmentEdits = useCallback((vals) => setAssignmentEdits(Array.isArray(vals) ? vals : []), []);

  // --- SAVE PIPELINE (matches old behavior, but reliably awaits requests) ---
  const saveDetailData = useCallback(
    async (job_id, data, pm_leader, additional_pms, current_pa_requirement_id) => {
      if (!job_id) return;

      showSnack('Saving...', 'info');

      try {
        const headers = await tokenHeaders();

        // 1) Save Project
        await Axios.patch(
          `${API_URL}projects/update/${job_id}/`,
          {
            ...data,
            sfdc_pm_leader: pm_leader || '',
            sfdc_additional_pms: additional_pms || '',
          },
          headers
        );

        // 2) Save Requirements (add/edit/delete) — await all
        if (assignmentEdits.length > 0) {
          const reqPromises = assignmentEdits.map((edit) => {
            const datesOverridden = !!edit.dates_overridden;
            const payload = {
              assignment_resource: edit.assignment_resource,
              requirement_type: edit.type,
              requirement_start_date: datesOverridden ? toYMD(edit.start) : null,
              requirement_end_date: datesOverridden ? toYMD(edit.end) : null,
              requirement_qty: edit.qty,
            };

            if (edit.delete === true && edit.requirement_id) {
              return Axios.delete(`${API_URL}requirement/${edit.requirement_id}/`, headers);
            }
            if (edit.requirement_id) {
              // update
              return Axios.patch(`${API_URL}requirement/${edit.requirement_id}/`, payload, headers);
            }
            // create
            return Axios.post(
              `${API_URL}requirement/`,
              { job_id, ...payload },
              headers
            );
          });

          await Promise.allSettled(reqPromises);
        }

        // 3) Sync PA requirement to follow PM Leader
        const new_pa_id = findPAIdForLeader(pm_leader);

        if (current_pa_requirement_id) {
          if (Number.isFinite(new_pa_id) && new_pa_id > 0) {
            // update existing PA requirement to the new PA id
            await Axios.patch(
              `${API_URL}requirement/${current_pa_requirement_id}/`,
              { assignment_resource: new_pa_id },
              headers
            );
          } else {
            // remove PA requirement if no PA should be set
            await Axios.delete(
              `${API_URL}requirement/${current_pa_requirement_id}/`,
              headers
            );
          }
        } else if (Number.isFinite(new_pa_id) && new_pa_id > 0) {
          // create PA requirement if missing
          await Axios.post(
            `${API_URL}requirement/`,
            {
              job_id,
              assignment_resource: new_pa_id,
              requirement_type: 'project_assistant',
              requirement_qty: 1,
            },
            headers
          );
        }

        // 4) Refresh upstream lists (keep prior logic)
        if (document.title === 'PM View') {
          if (typeof updateDetailView === 'function') {
            await updateDetailView(detail_view_project_info?.id);
          }
          if (typeof fetchPMViewData === 'function') {
            await fetchPMViewData();
          }
        } else if (document.title === 'Project View') {
          if (typeof fetchData === 'function') {
            await fetchData();
          }
        }

        showSnack('Change Saved Successfully', 'success');
      } catch (err) {
        showSnack('Error Saving Change', 'error');
      }
    },
    [
      tokenHeaders,
      toYMD,
      assignmentEdits,
      showSnack,
      updateDetailView,
      fetchPMViewData,
      fetchData,
      detail_view_project_info?.id,
      findPAIdForLeader,
    ]
  );

  const hasDetail = useMemo(
    () => !!detail_view_project_info && Object.keys(detail_view_project_info).length > 0,
    [detail_view_project_info]
  );

  return (
    <Root>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={snackbar.open}
        autoHideDuration={15000}
        onClose={closeSnack}
      >
        <MuiAlert elevation={6} variant="filled" severity={snackbar.type}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {hasDetail && (
        <ProjectDetailView
          loading={loading}
          detail_view_project_info={detail_view_project_info}
          toggleDetailView={toggleDetailView}
          pmResourceNames={pmResourceNames}
          profile_data={profile_data}
          // Local project edit buffer + setter (same API as before)
          projectEdits={projectEdits}
          updateProjectEdits={updateProjectEdits}
          // Local assignment edit buffer + setter (same API as before)
          assignmentEdits={assignmentEdits}
          updateAssignmentEdits={updateAssignmentEdits}
          // Save handler (same name/signature as before)
          saveDetailData={saveDetailData}
          // Probability flag passthrough
          multiply_by_sfdc_probability={multiply_by_sfdc_probability}
        />
      )}
    </Root>
  );
}

export default ProjectDetailedViewContainer;